import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config';
import { Loader2, Pencil, Download, Trash2, MessageCircle, X, Check, Image as ImageIcon, Sparkles } from 'lucide-react';

const texts = {
  ru: {
    title: 'Мои визуализации',
    subtitle: 'История ваших AI-генераций. Лимит — последние 30 на аккаунт.',
    empty: 'Ваши визуализации появятся здесь после генерации в конструкторе',
    goToConstructor: 'Перейти в конструктор',
    defaultName: (d) => `Визуализация от ${d}`,
    filterAll: 'Все дилеры',
    filterLabel: 'Фильтр по дилеру:',
    rename: 'Переименовать',
    download: 'Скачать',
    whatsapp: 'Отправить в WhatsApp',
    delete: 'Удалить',
    save: 'Сохранить',
    cancel: 'Отмена',
    confirmDelete: 'Удалить эту визуализацию? Действие необратимо.',
    deleting: 'Удаление...',
    waMsg: 'Здравствуйте! Посмотрите визуализацию интерьера: ',
    panel: 'Цвет панели',
    room: 'Помещение',
    of: 'из',
    error: 'Ошибка',
    loading: 'Загрузка галереи...',
    close: 'Закрыть',
    rooms: {
      hospital: 'Больница', school: 'Школа', kindergarten: 'Детский сад',
      office: 'Офис', admin: 'Госучреждение', residential: 'Жилой дом',
      hotel: 'Гостиница', pool: 'Бассейн', gym: 'Спортзал',
      dormitory: 'Общежитие', auditorium: 'Зал', station: 'Вокзал',
    },
  },
  kz: {
    title: 'Менің визуализацияларым',
    subtitle: 'AI-генерацияларыңыздың тарихы. Лимит — соңғы 30 аккаунтқа.',
    empty: 'Визуализацияларыңыз конструкторда жасалғаннан кейін осы жерде пайда болады',
    goToConstructor: 'Конструкторға өту',
    defaultName: (d) => `${d} визуализациясы`,
    filterAll: 'Барлық дилерлер',
    filterLabel: 'Дилер бойынша сүзгі:',
    rename: 'Қайта атау',
    download: 'Жүктеп алу',
    whatsapp: 'WhatsApp-ке жіберу',
    delete: 'Жою',
    save: 'Сақтау',
    cancel: 'Болдырмау',
    confirmDelete: 'Бұл визуализацияны жою керек пе? Бұл әрекетті болдырмауға болмайды.',
    deleting: 'Жойылуда...',
    waMsg: 'Сәлеметсіз бе! Интерьер визуализациясын қараңыз: ',
    panel: 'Панель түсі',
    room: 'Бөлме',
    of: '/',
    error: 'Қате',
    loading: 'Галерея жүктелуде...',
    close: 'Жабу',
    rooms: {
      hospital: 'Аурухана', school: 'Мектеп', kindergarten: 'Балабақша',
      office: 'Кеңсе', admin: 'Мемлекеттік мекеме', residential: 'Тұрғын үй',
      hotel: 'Қонақ үй', pool: 'Бассейн', gym: 'Спортзал',
      dormitory: 'Жатақхана', auditorium: 'Зал', station: 'Вокзал',
    },
  },
  en: {
    title: 'My Visualizations',
    subtitle: 'History of your AI generations. Limit — last 30 per account.',
    empty: 'Your visualizations will appear here after you generate in the constructor',
    goToConstructor: 'Go to constructor',
    defaultName: (d) => `Visualization of ${d}`,
    filterAll: 'All dealers',
    filterLabel: 'Filter by dealer:',
    rename: 'Rename',
    download: 'Download',
    whatsapp: 'Send via WhatsApp',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    confirmDelete: 'Delete this visualization? This action cannot be undone.',
    deleting: 'Deleting...',
    waMsg: 'Hello! Take a look at the interior visualization: ',
    panel: 'Panel color',
    room: 'Room',
    of: 'of',
    error: 'Error',
    loading: 'Loading gallery...',
    close: 'Close',
    rooms: {
      hospital: 'Hospital', school: 'School', kindergarten: 'Kindergarten',
      office: 'Office', admin: 'Government', residential: 'Residential',
      hotel: 'Hotel', pool: 'Pool', gym: 'Gym',
      dormitory: 'Dormitory', auditorium: 'Auditorium', station: 'Station',
    },
  },
};

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const locale = lang === 'ru' ? 'ru-RU' : lang === 'kz' ? 'kk-KZ' : 'en-US';
    return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch (_) {
    return '';
  }
};

export default function MyVisualizationsGallery({ token, userRole, onNavigateToConstructor }) {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;

  const isPrivileged = userRole === 'admin' || userRole === 'manager';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dealerFilter, setDealerFilter] = useState(''); // empty = all
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // url string of selected image
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '30' });
      if (isPrivileged && dealerFilter) {
        params.set('dealer_email', dealerFilter);
      }
      const url = `${API_URL}/api/constructor/gallery?${params.toString()}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError(`${t.error}: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [token, isPrivileged, dealerFilter, t.error]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const dealerOptions = useMemo(() => {
    if (!isPrivileged) return [];
    const set = new Set();
    items.forEach((it) => { if (it.user_email) set.add(it.user_email); });
    return Array.from(set).sort();
  }, [items, isPrivileged]);

  const startRename = (item) => {
    setEditingId(item.job_id);
    setEditValue(item.custom_name || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveRename = async (jobId) => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    setSavingId(jobId);
    try {
      const r = await fetch(`${API_URL}/api/constructor/jobs/${jobId}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ custom_name: trimmed }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setItems((prev) => prev.map((it) => (it.job_id === jobId ? { ...it, custom_name: data.custom_name } : it)));
      setEditingId(null);
      setEditValue('');
    } catch (e) {
      setError(`${t.error}: ${e.message || e}`);
    } finally {
      setSavingId(null);
    }
  };

  const performDelete = async (jobId) => {
    setDeletingId(jobId);
    try {
      const r = await fetch(`${API_URL}/api/constructor/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      setItems((prev) => prev.filter((it) => it.job_id !== jobId));
      setConfirmDeleteId(null);
    } catch (e) {
      setError(`${t.error}: ${e.message || e}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Build an absolute URL for an image_url returned by the API.
  // On prod REACT_APP_BACKEND_URL is empty (same-origin deploy), so we need
  // window.location.origin to produce a shareable link.
  const toAbsoluteUrl = (relOrAbs) => {
    if (!relOrAbs) return '';
    if (relOrAbs.startsWith('http')) return relOrAbs;
    const base = API_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}${relOrAbs}`;
  };

  const handleWhatsApp = (item) => {
    const absUrl = toAbsoluteUrl(item.image_url);
    const msg = encodeURIComponent(`${t.waMsg}${absUrl}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  const renderRoomLabel = (item) => {
    if (!item.room_type) return null;
    return t.rooms[item.room_type] || item.room_type;
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="gallery-root">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0E2A47] flex items-center gap-2" data-testid="gallery-title">
            <ImageIcon className="w-7 h-7 text-[#8b4444]" />
            {t.title}
          </h2>
          <p className="text-sm text-gray-600 mt-1">{t.subtitle}</p>
        </div>
        {isPrivileged && dealerOptions.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">{t.filterLabel}</label>
            <select
              value={dealerFilter}
              onChange={(e) => setDealerFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-[#0E2A47] focus:outline-none focus:border-[#8b4444]"
              data-testid="gallery-dealer-filter"
            >
              <option value="">{t.filterAll}</option>
              {dealerOptions.map((email) => (
                <option key={email} value={email}>{email}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="gallery-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500" data-testid="gallery-loading">
          <Loader2 className="w-10 h-10 animate-spin text-[#8b4444] mb-3" />
          <p>{t.loading}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 sm:py-24 text-center" data-testid="gallery-empty">
          <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
          <p className="text-gray-600 max-w-md mb-5">{t.empty}</p>
          {onNavigateToConstructor && (
            <button
              onClick={onNavigateToConstructor}
              className="px-5 py-2.5 bg-[#8b4444] hover:bg-[#7a3838] text-white rounded-xl flex items-center gap-2 transition-colors"
              data-testid="gallery-go-constructor"
            >
              <Sparkles className="w-4 h-4" />
              {t.goToConstructor}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="gallery-grid">
          {items.map((item) => {
            const absUrl = toAbsoluteUrl(item.image_url);
            const displayName = item.custom_name || t.defaultName(formatDate(item.created_at, language));
            const isEditing = editingId === item.job_id;
            const isSaving = savingId === item.job_id;
            const isDeleting = deletingId === item.job_id;
            return (
              <div
                key={item.job_id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`gallery-card-${item.job_id}`}
              >
                <button
                  type="button"
                  className="block w-full aspect-video bg-gray-100 overflow-hidden focus:outline-none"
                  onClick={() => setLightbox(absUrl)}
                  data-testid={`gallery-thumb-${item.job_id}`}
                >
                  <img
                    src={absUrl}
                    alt={displayName}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </button>
                <div className="p-3">
                  {isEditing ? (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename(item.job_id);
                          if (e.key === 'Escape') cancelRename();
                        }}
                        maxLength={100}
                        autoFocus
                        className="flex-1 px-2 py-1 text-sm border border-[#8b4444] rounded text-[#0E2A47] focus:outline-none"
                        data-testid={`gallery-rename-input-${item.job_id}`}
                      />
                      <button
                        onClick={() => saveRename(item.job_id)}
                        disabled={isSaving || !editValue.trim()}
                        className="p-1.5 bg-[#8b4444] hover:bg-[#7a3838] disabled:opacity-50 text-white rounded transition-colors"
                        title={t.save}
                        data-testid={`gallery-rename-save-${item.job_id}`}
                      >
                        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={cancelRename}
                        className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                        title={t.cancel}
                        data-testid={`gallery-rename-cancel-${item.job_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <h3 className="font-semibold text-[#0E2A47] text-sm mb-1 truncate" title={displayName}>
                      {displayName}
                    </h3>
                  )}
                  <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                    {item.panel_name && (
                      <div>{t.panel}: <span className="text-gray-700">{item.panel_name}</span></div>
                    )}
                    {renderRoomLabel(item) && (
                      <div>{t.room}: <span className="text-gray-700">{renderRoomLabel(item)}</span></div>
                    )}
                    {isPrivileged && item.user_email && (
                      <div className="text-[#8b4444]">{item.user_email}</div>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-1 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => startRename(item)}
                      disabled={isEditing}
                      className="p-2 hover:bg-gray-100 text-gray-600 hover:text-[#8b4444] rounded-lg transition-colors"
                      title={t.rename}
                      data-testid={`gallery-rename-btn-${item.job_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <a
                      href={absUrl}
                      download={`${displayName}.jpg`}
                      className="p-2 hover:bg-gray-100 text-gray-600 hover:text-[#8b4444] rounded-lg transition-colors"
                      title={t.download}
                      data-testid={`gallery-download-btn-${item.job_id}`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleWhatsApp(item)}
                      className="p-2 hover:bg-gray-100 text-gray-600 hover:text-[#25D366] rounded-lg transition-colors"
                      title={t.whatsapp}
                      data-testid={`gallery-whatsapp-btn-${item.job_id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(item.job_id)}
                      disabled={isDeleting}
                      className="p-2 hover:bg-red-50 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
                      title={t.delete}
                      data-testid={`gallery-delete-btn-${item.job_id}`}
                    >
                      {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox modal */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full"
            title={t.close}
            data-testid="gallery-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightbox}
            alt="Full size visualization"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Delete confirm dialog */}
      {confirmDeleteId && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmDeleteId(null)}
          data-testid="gallery-confirm-dialog"
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[#0E2A47] mb-5">{t.confirmDelete}</p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                data-testid="gallery-confirm-cancel"
              >
                {t.cancel}
              </button>
              <button
                onClick={() => performDelete(confirmDeleteId)}
                disabled={deletingId === confirmDeleteId}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center gap-2"
                data-testid="gallery-confirm-delete"
              >
                {deletingId === confirmDeleteId && <Loader2 className="w-4 h-4 animate-spin" />}
                {deletingId === confirmDeleteId ? t.deleting : t.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
